import { motion } from "framer-motion";

import { useParallax } from "../hooks/useParallax";

import { ScrollReveal } from "./ScrollReveal";
import { SectionHead } from "./SectionHead";

/**
 * The "service to society" surface — real photography as plates,
 * captions set under the frame, and the areas of work as one plain
 * line of text rather than a wall of chips.
 */
const SERVICE_PHOTOS: { src: string; tag: string; caption: string }[] = [
  {
    src: "/images/mahamandir/mahamandir-interior.jpg",
    tag: "Gathering",
    caption: "The Swarved Mahamandir, where the practice is held in common",
  },
  {
    src: "/images/nature/nature-still-water.jpg",
    tag: "Stewardship",
    caption: "Environmental and humanitarian initiatives across India",
  },
  {
    src: "/images/nature/light-through-trees.jpg",
    tag: "Service",
    caption: "Free schooling, medical camps, blood drives in over 50 countries",
  },
];

const SERVICE_AREAS = [
  "Health & wellness",
  "Free education",
  "Environmental care",
  "Humanitarian service",
  "Blood donation",
  "Women's empowerment",
];

export function Ripple() {
  const middle = useParallax(20);

  return (
    <section className="relative overflow-hidden bg-cream-dark px-7 py-24 md:py-32">
      <div className="mx-auto max-w-6xl">
        <SectionHead index="05" label="The Ripple" />

        <div className="mt-14 grid gap-12 md:mt-20 md:grid-cols-12 md:gap-10">
          <ScrollReveal className="md:col-span-5">
            <h2 className="font-display text-3xl leading-[1.22] text-warm-brown md:text-[1.9rem]">
              What begins within,
              <br />
              <span className="font-serif text-gold italic">
                flows outward.
              </span>
            </h2>
          </ScrollReveal>

          <ScrollReveal delay={0.15} className="md:col-span-7">
            <p className="max-w-xl text-base leading-[1.8] text-warm-brown-light md:text-[1.0625rem]">
              Vihangam Yog organisations worldwide serve communities through
              health initiatives, free education, environmental stewardship, and
              humanitarian service — guided by the principle that inner
              transformation naturally leads to service for society.
            </p>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={0.2}>
          <div className="mt-16 grid gap-6 md:mt-20 md:grid-cols-3">
            {SERVICE_PHOTOS.map((photo, i) => {
              const figure = (
                <figure className="plate">
                  <div className="aspect-[4/5] w-full overflow-hidden">
                    <img
                      src={photo.src}
                      alt={`${photo.tag} — ${photo.caption}`}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <figcaption className="plate-caption">
                    {photo.tag} · {photo.caption}
                  </figcaption>
                </figure>
              );
              // The middle plate drifts a touch heavier than its
              // neighbours — enough depth to feel, not to notice.
              return i === 1 ? (
                <motion.div
                  key={photo.src}
                  ref={middle.ref}
                  style={{ y: middle.y }}
                >
                  {figure}
                </motion.div>
              ) : (
                <div key={photo.src}>{figure}</div>
              );
            })}
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.3}>
          <p className="mx-auto mt-14 max-w-3xl text-center text-base leading-[1.8] text-warm-brown-light italic md:text-[1.0625rem]">
            {SERVICE_AREAS.join("  ·  ")}
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.4}>
          <p className="mt-10 text-center text-sm leading-relaxed text-warm-gray">
            SSDVYS holds Special Consultative Status with the UN ECOSOC since
            2013 · Present in over 50 countries · Transforming over 5 million
            lives
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}
